/**
 * Is an automotive vehicle specified completely enough to price?
 *
 * The service catalog is keyed on trim and body style — `id_car_trim` selects
 * the row, `body_style` resolves the vehicle SIZE the prices are banded by. A
 * half-specified vehicle does not fail loudly downstream: the catalog fetch
 * simply omits the size filter, the server returns every size, and the estimator
 * sees each service listed several times. So the Next button is the last place
 * this can be caught cheaply.
 *
 * The dropdowns carry their own placeholder strings as VALUES — 'Trim',
 * 'Style', 'Model', 'Make', 'Year', 'Color'. Deselecting writes the placeholder
 * rather than an empty string, so a bare truthiness check reads a deselected
 * dropdown as filled. Both are rejected here.
 *
 * Marine assets have their own predicate (`isMarineVehicleComplete` in
 * marineService.js) — a boat has no trim, body style or id_car_trim, so this
 * one would block it forever.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/vehicleCompleteness.test.mjs`. Do not add imports.
 */

/** Everything the catalog needs. Colour and VIN are deliberately absent. */
export const AUTO_REQUIRED_FIELDS = [
  'year', 'make', 'model', 'trim', 'body', 'body_style', 'id_car_trim',
];

/**
 * The placeholder each dropdown holds when nothing is chosen. Matched WHOLE,
 * case- and whitespace-insensitively — a substring test would reject the real
 * trim "Trimline GT" and the real model "Model 3".
 */
const PLACEHOLDERS = ['year', 'make', 'model', 'trim', 'style', 'body', 'color'];

function isFilled(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return value > 0;
  const v = String(value).trim();
  if (v === '') return false;
  if (PLACEHOLDERS.includes(v.toLowerCase())) return false;
  // Numeric ids arriving as strings: '0' is as absent as 0.
  if (/^\d+$/.test(v)) return Number(v) > 0;
  return true;
}

export function isAutoVehicleComplete(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return false;
  return AUTO_REQUIRED_FIELDS.every((field) => isFilled(vehicle[field]));
}
